"use client";

import { Button } from "@/components/ui/button";
import { type CtaKind, useCtaTracking } from "@/hooks/useCtaTracking";
import {
  addRegistrationSourceParam,
  resolveCurrentDiscoverTrafficSource,
} from "@/lib/analytics/discover-attribution";
import { normalizeUrl } from "@/lib/utils";
import type { EventSourceType } from "@/types/discover";
import { useSearchParams } from "next/navigation";

type CtaKindProps =
  | { ctaKind: "club"; clubId: string }
  | { ctaKind: Exclude<CtaKind, "club">; clubId?: string | null };

type CtaProps = {
  label: string;
  disabled?: boolean;
  className?: string;
  eventId?: string | null;
  groupId?: string | null;
  sourceType?: EventSourceType;
  /**
   * Tag the outbound URL with the visitor's registration source. Resolved here
   * rather than server-side because this page's HTML is cached, so a server read
   * of `traffic_source` comes back empty and every registration would look
   * organic.
   */
  withRegistrationSource?: boolean;
  variant?:
    | "default"
    | "secondary"
    | "outline"
    | "ghost"
    | "destructive"
    | "brand";
} & CtaKindProps &
  (
    | {
        href: string;
        onClick?: undefined;
      }
    | {
        href?: undefined;
        onClick: React.MouseEventHandler;
      }
  );

export default function Cta({
  href,
  onClick,
  label,
  disabled = false,
  variant = "brand",
  className = "",
  ctaKind,
  clubId,
  eventId,
  groupId,
  sourceType,
  withRegistrationSource = false,
}: CtaProps) {
  const searchParams = useSearchParams();
  const targetHref =
    href && withRegistrationSource
      ? addRegistrationSourceParam(
          href,
          resolveCurrentDiscoverTrafficSource(searchParams),
        )
      : href;

  const { trackCtaClick } = useCtaTracking({
    ctaKind,
    clubId: clubId ?? null,
    eventId,
    groupId,
    href: targetHref,
    sourceType,
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    trackCtaClick();

    if (targetHref) {
      window.open(normalizeUrl(targetHref), "_blank");
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Button
      variant={variant}
      className={`text-md font-medium w-full rounded-full py-2 px-6 h-12 ${className}`}
      disabled={disabled}
      onClick={handleClick}
    >
      {label}
    </Button>
  );
}
